import React, { useRef, useLayoutEffect, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { createPopper } from '@popperjs/core'

import { useClickOutside } from 'hooks/dom'
import { Dialog } from '../Dialog/Dialog'

export const PLACEMENT = Object.freeze({
  auto: 'auto',
  autoStart: 'auto-start',
  autoEnd: 'auto-end',
  top: 'top',
  topStart: 'top-start',
  topEnd: 'top-end',
  bottom: 'bottom',
  bottomStart: 'bottom-start',
  bottomEnd: 'bottom-end',
  right: 'right',
  rightStart: 'right-start',
  rightEnd: 'right-end',
  left: 'left',
  leftStart: 'left-start',
  leftEnd: 'left-end'
})

export const STRATEGY = {
  fixed: 'fixed',
  absolute: 'absolute'
}

export const Popper = ({
  anchor,
  children,
  placement,
  isOpen,
  className,
  strategy,
  modifiers,
  onClickOutside,
  mouseOutContent,
  mouseOverContent
}) => {
  const dialogRef = useRef()
  const popperRef = useRef()
  const register = useCallback(() => {
    const dialog = dialogRef.current

    if (anchor && dialog) {
      if (popperRef.current) {
        popperRef.current.destroy()
      }

      popperRef.current = createPopper(anchor, dialog, {
        placement,
        strategy,
        modifiers
      })
    }
  }, [anchor, placement, strategy, modifiers])

  const registerRef = useCallback(
    ref => {
      dialogRef.current = ref

      if (anchor) {
        register()
      }
    },
    [anchor, register]
  )

  useLayoutEffect(() => register(), [register])

  // Unregister popper when unmounting
  useEffect(() => {
    const popper = popperRef?.current
    return () => popper?.destroy()
  }, [])

  useEffect(() => {
    const el = dialogRef.current
    el?.addEventListener('mouseover', mouseOverContent)
    el?.addEventListener('mouseleave', mouseOutContent)

    return () => {
      el?.removeEventListener('mouseover', mouseOverContent)
      el?.removeEventListener('mouseleave', mouseOutContent)
    }
  }, [dialogRef, mouseOutContent, mouseOverContent])

  useClickOutside(onClickOutside, [dialogRef, anchor])

  return (
    <Dialog className={className} isOpen={isOpen} ref={registerRef}>
      {children}
    </Dialog>
  )
}

Popper.defaultProps = {
  anchor: undefined,
  className: undefined,
  children: undefined,
  placement: 'bottom-start',
  strategy: 'absolute',
  onClickOutside: undefined,
  mouseOutContent: undefined,
  mouseOverContent: undefined,
  modifiers: []
}

Popper.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  anchor: PropTypes.instanceOf(Element),
  isOpen: PropTypes.bool.isRequired,
  placement: PropTypes.oneOf(Object.values(PLACEMENT)),
  strategy: PropTypes.oneOf(Object.values(STRATEGY)),
  onClickOutside: PropTypes.func,
  mouseOutContent: PropTypes.func,
  mouseOverContent: PropTypes.func,
  modifiers: PropTypes.arrayOf(PropTypes.object)
}
